import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const MediaMonitoring = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');

  const mentions = [
  {
    id: 1,
    source: 'BookWorld Magazine',
    title: 'PenGame Publishing Announces New Fantasy Series',
    snippet: 'The innovative publishing house continues to expand its fantasy catalog with groundbreaking new authors...',
    sentiment: 'positive',
    timestamp: new Date(Date.now() - 1800000),
    reach: 45000,
    engagement: 1200,
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0f36935-1761935665552.png",
    logoAlt: 'BookWorld Magazine circular logo with open book icon in blue and white'
  },
  {
    id: 2,
    source: 'Literary Review Today',
    title: 'Rising Stars in Independent Publishing',
    snippet: 'PenGame Publishing has emerged as a leader in discovering fresh talent, with their recent acquisitions showing...',
    sentiment: 'positive',
    timestamp: new Date(Date.now() - 3600000),
    reach: 28000,
    engagement: 890,
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1cf43c2a7-1761935669728.png",
    logoAlt: 'Literary Review Today logo featuring stylized quill pen and scroll design'
  },
  {
    id: 3,
    source: 'Publishing Weekly',
    title: 'Market Analysis: Small Publishers Making Big Waves',
    snippet: 'Among the notable mentions, PenGame Publishing stands out for their innovative approach to author development...',
    sentiment: 'neutral',
    timestamp: new Date(Date.now() - 7200000),
    reach: 67000,
    engagement: 2100,
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_14a4f494f-1761935665980.png",
    logoAlt: 'Publishing Weekly magazine logo with modern typography and newspaper fold design'
  },
  {
    id: 4,
    source: 'Author Spotlight Blog',
    title: 'Interview: Success Stories from PenGame Authors',
    snippet: 'We spoke with several authors who have found success through PenGame Publishing\'s unique mentorship program...',
    sentiment: 'positive',
    timestamp: new Date(Date.now() - 10800000),
    reach: 12000,
    engagement: 450,
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_155190518-1761935670442.png",
    logoAlt: 'Author Spotlight Blog logo with spotlight beam illuminating typewriter keys'
  }];


  const keywords = [
  { term: 'PenGame Publishing', mentions: 24, trend: 'up' },
  { term: 'Fantasy Series', mentions: 18, trend: 'up' },
  { term: 'New Authors', mentions: 12, trend: 'stable' },
  { term: 'Book Launch', mentions: 9, trend: 'down' }];


  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive':return 'text-success bg-success/10';
      case 'negative':return 'text-error bg-error/10';
      default:return 'text-muted-foreground bg-muted/30';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive':return 'ThumbsUp';
      case 'negative':return 'ThumbsDown';
      default:return 'Minus';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / 3600000);

    if (hours < 1) return 'Less than 1h ago';
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const filteredMentions = selectedFilter === 'all' ?
  mentions :
  mentions?.filter((mention) => mention?.sentiment === selectedFilter);

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Search" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Media Monitoring</h3>
              <p className="text-sm text-muted-foreground">Track mentions and sentiment analysis</p>
            </div>
          </div>
          
          <Button variant="outline" size="sm" iconName="Settings">
            Configure Alerts
          </Button>
        </div>
        
        <div className="flex items-center space-x-2">
          {['all', 'positive', 'neutral', 'negative']?.map((filter) =>
          <Button
            key={filter}
            variant={selectedFilter === filter ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setSelectedFilter(filter)}>

              {filter?.charAt(0)?.toUpperCase() + filter?.slice(1)}
            </Button>
          )}
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-sm font-medium text-foreground mb-3">Recent Mentions</h4>
            {filteredMentions?.map((mention) =>
            <div key={mention?.id} className="p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors duration-200">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                    <Image
                    src={mention?.logo}
                    alt={mention?.logoAlt}
                    className="w-full h-full object-cover" />

                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <h5 className="text-sm font-medium text-foreground">{mention?.source}</h5>
                        <div className={`px-2 py-1 rounded-full flex items-center space-x-1 ${getSentimentColor(mention?.sentiment)}`}>
                          <Icon name={getSentimentIcon(mention?.sentiment)} size={12} />
                          <span className="text-xs font-medium capitalize">{mention?.sentiment}</span>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatTimestamp(mention?.timestamp)}</span>
                    </div>
                    
                    <h6 className="text-sm font-medium text-foreground mb-2">{mention?.title}</h6>
                    <p className="text-sm text-muted-foreground mb-3">{mention?.snippet}</p>
                    
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <Icon name="Eye" size={12} />
                        <span>{mention?.reach?.toLocaleString()} reach</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="MessageCircle" size={12} />
                        <span>{mention?.engagement?.toLocaleString()} engagement</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Trending Keywords</h4>
              <div className="space-y-3">
                {keywords?.map((keyword, index) =>
                <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-foreground">{keyword?.term}</p>
                      <p className="text-xs text-muted-foreground">{keyword?.mentions} mentions</p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon
                      name={keyword?.trend === 'up' ? 'TrendingUp' : keyword?.trend === 'down' ? 'TrendingDown' : 'Minus'}
                      size={14}
                      className={
                      keyword?.trend === 'up' ? 'text-success' :
                      keyword?.trend === 'down' ? 'text-error' : 'text-muted-foreground'
                      } />

                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Sentiment Overview</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Positive</span>
                  <span className="text-sm font-medium text-success">75%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-success h-2 rounded-full" style={{ width: '75%' }}></div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Neutral</span>
                  <span className="text-sm font-medium text-muted-foreground">20%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-muted-foreground h-2 rounded-full" style={{ width: '20%' }}></div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Negative</span>
                  <span className="text-sm font-medium text-error">5%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-error h-2 rounded-full" style={{ width: '5%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default MediaMonitoring;